<template>
    <!-- Name -->
    <input-generic
    v-if="!isHolder"
    :id="`name_${index}`"
    :params="nameParams"
    :inputValue="insuredPerson.name"
    @input="[emit('insuredPersonDataChange', { field: 'name', inputValue: $event, index: index}), clearFieldError('nameParams')]"/>
    <text-block-generic
    v-else
    :params="{
        label: nameParams.labelText,
        content: titleCaseBuilder(insuredPerson.name)
    }"/>
    <!-- Birth Date -->
    <input-calendar
    v-if="!isHolder"
    :id="`birthDate_${index}`"
    :params="birthDateParams"
    :disabledDates="{
        to: null,
        from: new Date()
    }"
    :inputValue="insuredPerson.birthDate"
    @input="[emit('insuredPersonDataChange', { field: 'birthDate', inputValue: $event, index: index}), clearFieldError('birthDateParams')]"/>
    <text-block-generic
    v-else
    :params="{
        label: birthDateParams.labelText,
        content: getDate(insuredPerson.birthDate),
        classes: 'capitalize'
    }"/>
    <!-- Identification -->
    <input-generic
    v-if="!isHolder"
    :id="`identification_${index}`"
    :params="identificationParams"
    :inputValue="insuredPerson.identification"
    @input="[emit('insuredPersonDataChange', { field: 'identification', inputValue: $event, index: index}), clearFieldError('identificationParams')]"/>
    <text-block-generic
    v-else
    :params="{
        label: identificationParams.labelText,
        content: insuredPerson.identification
    }"/>
    <!-- Fiscal Identification -->
    <input-generic
    v-if="!isHolder"
    :id="`fiscalIdentification_${index}`"
    :params="fiscalIdentificationParams"
    :inputValue="insuredPerson.fiscalIdentification"
    @input="[emit('insuredPersonDataChange', { field: 'fiscalIdentification', inputValue: $event, index: index}), emit('removeStepValidationError', `fiscalIdentification_${index}`), clearFieldError('fiscalIdentificationParams')]"
    @onBlur="fiscalIdentificationValidation"/>
    <text-block-generic
    v-else
    :params="{
        label: fiscalIdentificationParams.labelText,
        content: insuredPerson.fiscalIdentification
    }"/>
    <!-- contact -->
    <input-generic
    v-if="!isHolder"
    :id="`contact_${index}`"
    :params="contactParams"
    :inputValue="insuredPerson.contact"
    @input="[emit('insuredPersonDataChange', { field: 'contact', inputValue: $event, index: index}), emit('removeStepValidationError', `contactPerson_${index}`), clearFieldError('contactParams')]"
    @onBlur="contactValidation"/>
    <dropdown-generic
    v-else
    :params="contactParams"
    :selectedOption="insuredPerson.contact"
    :options="getHolderData.contactList.map(el => {
        return { id: el.phone, text: el.phone }
    })"
    @clicked="emit('insuredPersonDataChange', { field: 'contact', inputValue: $event, index: index})"/>
    <!-- secondaryMobile -->
    <input-generic
    :id="`secondaryMobile_${index}`"
    :params="secondaryMobileParams"
    :inputValue="insuredPerson.secondaryMobile"
    @input="[emit('insuredPersonDataChange', { field: 'secondaryMobile', inputValue: $event, index: index}), emit('removeStepValidationError', `mobilePerson_${index}`), clearFieldError('secondaryMobileParams')]"
    @onBlur="mobileValidation"/>
    <!-- email -->
    <input-generic
    :id="`email_${index}`"
    :params="emailParams"
    :inputValue="insuredPerson.email"
    @input="[emit('insuredPersonDataChange', { field: 'email', inputValue: $event.trim(), index: index}), emit('removeStepValidationError', `emailPerson_${index}`), clearFieldError('emailParams')]"
    @onBlur="emailValidation"/>
    <!-- Gender -->
    <dropdown-generic
    v-if="!isHolder"
    :id="`gender_${index}`"
    :params="genderParams"
    :selectedOption="insuredPerson.gender"
    :options="getDomainData('Gender').map(ele => {
        return { id: ele.code, text: ele.description }
    })"
    @clicked="[emit('insuredPersonDataChange', { field: 'gender', inputValue: $event, index: index}), clearFieldError('genderParams')]"/>
    <text-block-generic
    v-else
    :params="{
        label: genderParams.labelText,
        content: getDomainData('Gender').find(ele => ele.code === insuredPerson.gender).description
    }"/>
    <!-- postal code -->
    <dropdown-generic
    v-if="!isHolder"
    :id="`postalCode_${index}`"
    :params="postalCodeParams"
    :selectedOption="insuredPerson.insuranceEntityAddress.postalCode"
    :options="getDomainData('Province').map(ele => {
        return { id: ele.code, text: ele.description }
    })"
    @clicked="postalCodeChangeHandler($event)"/>
    <text-block-generic
    v-else
    :params="{
        label: postalCodeParams.labelText,
        content: getDomainData('Province').find(ele => ele.code === insuredPerson.insuranceEntityAddress.postalCode).description
    }"/>
    <!-- province -->
    <dropdown-generic
    v-if="!isHolder && insuredPerson.insuranceEntityAddress.postalCode"
    :id="`province_${index}`"
    :params="provinceParams"
    :selectedOption="insuredPerson.insuranceEntityAddress.province"
    :options="getDomainData('City').map(ele => {
        return { id: ele.code, text: ele.description }
    })"
    @clicked="[emit('insuredPersonDataChange', { field: 'insuranceEntityAddress', subField: 'province', inputValue: $event, index: index}), clearFieldError('provinceParams')]"/>
    <text-block-generic
    v-if="isHolder && getDomainData('City').length"
    :params="{
        label: provinceParams.labelText,
        content: getDomainData('City').find(ele => ele.code === insuredPerson.insuranceEntityAddress.province).description
    }"/>
    <!-- country -->
    <dropdown-generic
    v-if="!isHolder"
    :id="`country_${index}`"
    :params="countryParams"
    :selectedOption="insuredPerson.insuranceEntityAddress.country"
    :options="getDomainData('AddressCountry').map(ele => {
        return { id: ele.code, text: ele.description }
    })"
    @clicked="[emit('insuredPersonDataChange', { field: 'insuranceEntityAddress', subField: 'country', inputValue: $event, index: index}), clearFieldError('countryParams')]"/>
    <text-block-generic
    v-else
    :params="{
        label: countryParams.labelText,
        content: getDomainData('AddressCountry').find(ele => ele.code === insuredPerson.insuranceEntityAddress.country).description
    }"/>
</template>

<script setup>
import InputGeneric from '@/components/generic/inputs/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import InputCalendar from '@/components/generic/inputs/Calendar'
import DropdownGeneric from '@/components/generic/dropdowns/generic/Generic'

/* Vue */
import { ref, computed, watch } from 'vue'

/* Assets */
import { PHONE_NUMBER, NIF } from '@/assets/constants/masks'
import { emailValidator, phoneNumberValidator } from '@/assets/scripts/validators'
import { getDate } from '@/assets/scripts/dateBuilders'
import { titleCaseBuilder } from '@/assets/scripts/strings'
import { getElementOffsetTopPosition } from '@/assets/scripts/htmlManipulators'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['insuredPersonDataChange', 'resetShowInsuredPersonErrorsProp', 'addStepValidationError', 'removeStepValidationError', 'scrollTo', 'showOverlayLoader', 'hideOverlayLoader', 'showError', 'showContainerError'])

/* mapActions & mapGetters */
const {	requestInsuranceDomainsList } = mapActions(moduleMapper.BANKASSURANCE_SUBSCRIBE)
const {	getDomainData, getHolderData } = mapGetters(moduleMapper.BANKASSURANCE_SUBSCRIBE)

/* props received
@insuredPerson { Object } - object with insured person info
*/
const props = defineProps({
    index: Number,
    isHolder: Boolean,
    insuredPerson: Object,
    showInsuredPersonErrors: Boolean
})

/**********************************************************************
*                                Name                                 *
**********************************************************************/
const nameParams = ref({
	labelText: t('bankassurance.subscribe.travel.step3.personNameLabel'),
    placeholder: t('bankassurance.subscribe.travel.step3.personNamePlaceholder'),
	mandatory: true,
    maxLength: 42,
	errorState: false,
    errorMessage: null
})

/**********************************************************************
*                              Birth Date                             *
**********************************************************************/
const birthDateParams = ref({
	labelText: t('bankassurance.subscribe.travel.step3.personBirthDateLabel'),
    placeholder: t('bankassurance.subscribe.travel.step3.personBirthDatePlaceholder'),
	mandatory: true,
	errorState: false,
    errorMessage: null
})

/**********************************************************************
*                            Identification                           *
**********************************************************************/
const identificationParams = ref({
	labelText: t('bankassurance.subscribe.travel.step3.personIdentificationLabel'),
    placeholder: t('bankassurance.subscribe.travel.step3.personIdentificationPlaceholder'),
	mandatory: true,
    maxLength: 14,
	errorState: false,
    errorMessage: null
})

/**********************************************************************
*                           Fiscal Identification                     *
**********************************************************************/
const fiscalIdentificationParams = ref({
	labelText: t('bankassurance.subscribe.travel.step3.personFiscalIdentificationLabel'),
    placeholder: t('bankassurance.subscribe.travel.step3.personFiscalIdentificationPlaceholder'),
	mandatory: true,
    mask: NIF,
    maxLength: 14,
	errorState: false,
    errorMessage: null
})

const fiscalIdentificationValidation = () => {
    if (!!props.insuredPerson.fiscalIdentification.length && props.insuredPerson.fiscalIdentification.length < 14) {
        fiscalIdentificationParams.value.errorState = true
        fiscalIdentificationParams.value.errorMessage = t('invalidFiscalIdentificationError')

        emit('addStepValidationError', `fiscalIdentification_${props.index}`)
    }
}

/**********************************************************************
*                                 Contact                             *
**********************************************************************/
const contactParams = ref({
	labelText: t('bankassurance.subscribe.travel.step3.personContactLabel'),
    placeholder: t('bankassurance.subscribe.travel.step3.personContactPlaceholder'),
	mandatory: true,
    mask: PHONE_NUMBER,
    maxLength: 14,
	errorState: false,
    errorMessage: null
})

const contactValidation = () => {
    if (!!props.insuredPerson.contact.length && !phoneNumberValidator(props.insuredPerson.contact)) {
        contactParams.value.errorState = true
        contactParams.value.errorMessage = t('mobilePhoneLengthOrError')

        emit('addStepValidationError', `contactPerson_${props.index}`)
    }
}

/**********************************************************************
*                                 Mobile                             *
**********************************************************************/
const secondaryMobileParams = ref({
	labelText: t('bankassurance.subscribe.travel.step3.personMobileLabel'),
    placeholder: t('bankassurance.subscribe.travel.step3.personMobilePlaceholder'),
	mandatory: false,
    mask: PHONE_NUMBER,
    maxLength: 14,
	errorState: false,
    errorMessage: null
})

const mobileValidation = () => {
    if (!!props.insuredPerson.secondaryMobile.length && !phoneNumberValidator(props.insuredPerson.secondaryMobile)) {
        secondaryMobileParams.value.errorState = true
        secondaryMobileParams.value.errorMessage = t('mobilePhoneLengthOrError')

        emit('addStepValidationError', `mobilePerson_${props.index}`)
    }
}

/**********************************************************************
*                                 Email                               *
**********************************************************************/
const emailParams = ref({
	labelText: t('bankassurance.subscribe.travel.step3.personEmailLabel'),
    placeholder: t('bankassurance.subscribe.travel.step3.personEmailPlaceholder'),
	mandatory: false,
    maxLength: 42,
	errorState: false,
    errorMessage: null
})

const emailValidation = () => {
    if (!!props.insuredPerson.email.length && !emailValidator(props.insuredPerson.email)) {
        emailParams.value.errorState = true
        emailParams.value.errorMessage = t('invalidEmailError')

        emit('addStepValidationError', `emailPerson_${props.index}`)
    }
}

/**********************************************************************
*                                 Gender                              *
**********************************************************************/
const genderParams = ref({
	labelText: t('bankassurance.subscribe.travel.step3.personGenderLabel'),
    placeholder: t('bankassurance.subscribe.travel.step3.personGenderPlaceholder'),
	mandatory: true,
	errorState: false,
    errorMessage: null
})

/**********************************************************************
*                                 Province                              *
**********************************************************************/
const provinceParams = ref({
	labelText: t('bankassurance.subscribe.travel.step3.personPostalCodeLabel'),
    placeholder: t('bankassurance.subscribe.travel.step3.personPostalCodePlaceholder'),
	mandatory: true,
	errorState: false,
    errorMessage: null
})

/**********************************************************************
*                              Postal Code                            *
**********************************************************************/
const postalCodeParams = ref({
	labelText: t('bankassurance.subscribe.travel.step3.personProvinceLabel'),
    placeholder: t('bankassurance.subscribe.travel.step3.personProvincePlaceholder'),
	mandatory: true,
	errorState: false,
    errorMessage: null
})

/**********************************************************************
*                                Country                              *
**********************************************************************/
const countryParams = ref({
	labelText: t('bankassurance.subscribe.travel.step3.personCountryLabel'),
    placeholder: t('bankassurance.subscribe.travel.step3.personCountryPlaceholder'),
	mandatory: true,
	errorState: false,
    errorMessage: null
})

/**********************************************************************
*                                                                     *
*                        MANDATORY FIELDS LOGIC                       *
*                                                                     *
***********************************************************************/
const allFormFields = ref({
	nameParams: nameParams.value,
    birthDateParams: birthDateParams.value,
    identificationParams: identificationParams.value,
    fiscalIdentificationParams: fiscalIdentificationParams.value,
    contactParams: contactParams.value,
    secondaryMobileParams: secondaryMobileParams.value,
    emailParams: emailParams.value,
    genderParams: genderParams.value,
    provinceParams: provinceParams.value,
    postalCodeParams: postalCodeParams.value,
    countryParams: countryParams.value
})

watch(() => props.showInsuredPersonErrors, newValue => {
	if (newValue) showMandatoryErrorsHandler()
    emit('resetShowInsuredPersonErrorsProp')
})

watch(() => props.isHolder, newValue => {
    if (newValue) postalCodeChangeHandler(props.insuredPerson.insuranceEntityAddress.postalCode)
})

const showMandatoryErrorsHandler = () => {
	/* Logic to scroll to first emptyField */

	const mandatoryFieldsEmptyList = []

    for (const key in props.insuredPerson) {
        if (key === 'insuranceEntityAddress') {
            for (const addressKey in props.insuredPerson[key]) {
                if (!props.insuredPerson[key][addressKey].length) mandatoryFieldsEmptyList.push(addressKey)
            }
        } else if (!props.insuredPerson[key].length && key !== 'secondaryMobile' && key !== 'email') mandatoryFieldsEmptyList.push(key)
    }

    if (mandatoryFieldsEmptyList.length) {
        emit('scrollTo', getElementOffsetTopPosition(`${mandatoryFieldsEmptyList[0]}_${props.index}`))

        mandatoryFieldsEmptyList.forEach(fieldName => {
            allFormFields.value[`${fieldName}Params`].errorState = true
            allFormFields.value[`${fieldName}Params`].errorMessage = t('mandatoryFieldError')
        })
    }
}

/**********************************************************************
*                                                                     *
*                          CLEAR ERRORS LOGIC                         *
*                                                                     *
***********************************************************************/
const formHasErrors = computed(() => {
    return Object.values(allFormFields.value).some(el => el.errorState)
})

watch(() => props.clearFormErrors, () => {
    clearFormErrors()
    emit('formErrorsCleared')
})

watch(() => props.isHolder, newValue => {
	if (newValue) clearFormErrors()
})

watch(() => formHasErrors.value, newValue => {
    emit('showContainerError', newValue)
    emit('scrollTo')
})

const clearFieldError = (fieldName) => {
    allFormFields.value[fieldName].errorMessage = null
    allFormFields.value[fieldName].errorState = false
}

const clearFormErrors = () => {
    for (var fieldName in allFormFields.value) {
        clearFieldError(fieldName)
    }
}

const postalCodeChangeHandler = async (data) => {
    emit('showOverlayLoader')

    try {
        await requestInsuranceDomainsList({ domain: 'City', params: { parentValue: data } })
        emit('insuredPersonDataChange', { field: 'insuranceEntityAddress', subField: 'postalCode', inputValue: data, index: props.index })
        clearFieldError('postalCodeParams')
        clearFieldError('provinceParams')

        emit('hideOverlayLoader')
    } catch (error) {
        emit('hideOverlayLoader')
        emit('showError', error.response.data)
    }
}
</script>

<style lang="scss" scoped>
</style>
